/**
 * Ageing and statements, rebuilt as a credit-control workspace on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `components/detail-parts.tsx`'s `PageHero`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the four figure cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   the statement card's icon + label/value blocks, through `InfoBlock`.
 *
 * The receivables table is `ReportTable` (the whole report arrives in one response and is paged in
 * memory) over `ageing-columns.tsx`, each row's ageing drawn by the domain `BucketBar`. The
 * screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 *
 * Rules this screen keeps:
 * - Every figure is the API's. There is no portfolio-wide bucket strip: the API returns per-customer
 *   figures only, and adding them up here would be the client computing a receivables total
 *   (constitution I). The over-limit count is the API's own `credit-breaches` count, read unawaited
 *   so a failed read blanks that one card — "Unreadable", never a 0.
 * - `CaveatBanner` wraps the receivables table itself, so the receipts caveat can never render
 *   after, or apart from, the figures it qualifies.
 * - Running statements emails real customers, so it still asks first (`ConfirmDialog`, FIN-07).
 */
import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useLoaderData, useOutletContext } from "react-router-dom";
import {
  PiCalendarCheckDuotone as CalendarIcon,
  PiEnvelopeSimpleDuotone as MailIcon,
  PiHandCoinsDuotone as WalletIcon,
  PiLockSimpleDuotone as LockIcon,
  PiPaperPlaneTiltDuotone as SendIcon,
  PiPlugsConnectedDuotone as PlugIcon,
  PiReceiptDuotone as ReceiptIcon,
  PiUsersThreeDuotone as UsersIcon,
  PiWarningOctagonDuotone as BreachIcon,
} from "react-icons/pi";
import { Alert, Badge, Button, Text } from "rizzui";
import { CaveatBanner, ConfirmDialog, EmptyState, ErrorSurface, MetricCard, WidgetCard } from "@steelyard/theme";
import { parseAgeingRows } from "../../components/customers/customer-ageing-table";
import { TileBoundary } from "../../components/dashboard/TileBoundary";
import { problemMessage } from "../../components/dashboard/tone";
import { CardTitle, InfoBlock, PageHero } from "../../components/detail-parts";
import { ReportTable } from "../../components/table/table-view";
import { getAgeing, getCreditBreaches, runStatements, type StatementRunResult } from "../../lib/services/customers";
import { formatDate, sentenceCase } from "../../lib/format";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { ageingColumns } from "./ageing-columns";

export async function ageingLoader() {
  const data = await getAgeing();
  // Deliberately not awaited — see the file header. The no-op `catch` only marks this promise as
  // handled; `Await` still receives the rejection and renders its own "Unreadable".
  const breaches = getCreditBreaches().then((result) => result.count);
  breaches.catch(() => {});
  return { rows: parseAgeingRows(data), breaches };
}

const EXPLANATION: Record<string, string> = {
  disabled: "Statements are switched off in Infra Junction's configuration. Nothing was sent.",
  not_due_today: "Today is not the configured statement day, so nothing was sent. This is not a failure — running the job on the wrong date is meant to do nothing.",
  due: "Today is the statement day and the run went ahead.",
};

export default function CustomersAgeing() {
  const { rows, breaches } = useLoaderData() as Awaited<ReturnType<typeof ageingLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [result, setResult] = useState<StatementRunResult | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<unknown>(null);
  const [confirming, setConfirming] = useState(false);

  const mayRun = can(permissionState, "customers.credit");
  const excluded = rows.some((row) => row.excludesReceipts);
  const lastApplied = rows.map((row) => row.receiptsLastAppliedAt).find((value) => typeof value === "string" && value !== "");

  async function handleRun() {
    setConfirming(false);
    setRunning(true);
    setRunError(null);
    try {
      const outcome = await runStatements();
      setResult(outcome);
      toast.success("Statement job run");
    } catch (err) {
      setRunError(err);
      toast.error("Could not run the statement job");
    } finally {
      setRunning(false);
    }
  }

  const receivables =
    rows.length === 0 ? (
      <WidgetCard rounded="xl" title={<ReceivablesTitle />} headerClassName="items-center">
        <div className="pt-5">
          <EmptyState title="Nothing outstanding">No customer has an unsettled balance.</EmptyState>
        </div>
      </WidgetCard>
    ) : (
      <ReportTable
        rows={rows}
        columns={ageingColumns}
        caption="Outstanding balance by customer, aged into the configured buckets"
        title={<ReceivablesTitle />}
        description={<Text className="mt-1 text-sm text-gray-500">Sort by outstanding to see who owes most. Buckets further right are older debt.</Text>}
        action={
          <Badge variant="flat" color="secondary" rounded="pill">
            {rows.length} {rows.length === 1 ? "customer" : "customers"}
          </Badge>
        }
        searchPlaceholder="Find a customer…"
        getRowId={(row) => row.customerId}
      />
    );

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Finance" }, { name: "Ageing" }]}
        icon={<WalletIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Ageing & Statements"
        description="What every customer owes, aged into the buckets Infra Junction’s configuration defines — and the monthly statement run that tells them."
        meta={
          <>
            <Badge variant="flat" color={excluded ? "danger" : "success"} rounded="pill" className="gap-1.5">
              <ReceiptIcon aria-hidden className="size-4" />
              {excluded ? "Receipts not applied" : "Receipts applied"}
            </Badge>
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <CalendarIcon aria-hidden className="size-4" />
              Buckets set by configuration
            </Badge>
          </>
        }
        actions={
          <>
            <Link to="/integrations/tally">
              <Button as="span" variant="outline">
                <PlugIcon aria-hidden className="me-1.5 size-4 text-secondary" />
                Receipts pull
              </Button>
            </Link>
            <Link to="/customers">
              <Button as="span" variant="outline">
                <UsersIcon aria-hidden className="me-1.5 size-4 text-blue" />
                All customers
              </Button>
            </Link>
          </>
        }
      >
        <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
          <MetricCard
            title="Customers on this report"
            metric={rows.length}
            icon={<UsersIcon aria-hidden className="size-6" />}
            iconClassName="bg-secondary text-white shadow-sm"
            info={<Text className="mt-0.5 text-xs text-gray-500">One row each, as the API returned them</Text>}
            rounded="lg"
            className="transition-shadow hover:shadow-md"
          />
          <TileBoundary
            resolve={breaches}
            fallbackClassName="h-[98px]"
            errorFallback={(error) => (
              <MetricCard
                title="Over credit limit"
                metric="Unreadable"
                icon={<BreachIcon aria-hidden className="size-6" />}
                iconClassName="bg-gray-100 text-gray-500"
                info={<Text className="mt-0.5 text-xs text-gray-500">{problemMessage(error, "Could not be read — not the same as none.")}</Text>}
                rounded="lg"
              />
            )}
          >
            {(count) => (
              <MetricCard
                title="Over credit limit"
                metric={count}
                icon={<BreachIcon aria-hidden className="size-6" />}
                iconClassName={count > 0 ? "bg-red text-white shadow-sm" : "bg-green text-white shadow-sm"}
                info={<Text className="mt-0.5 text-xs text-gray-500">{count > 0 ? "Chase these before extending more credit" : "No customer is past their limit"}</Text>}
                rounded="lg"
                className="transition-shadow hover:shadow-md"
              />
            )}
          </TileBoundary>
          <MetricCard
            title="Receipts"
            metric={excluded ? "Not applied" : rows.length === 0 ? "Nothing to qualify" : "Applied"}
            icon={<ReceiptIcon aria-hidden className="size-6" />}
            iconClassName={excluded ? "bg-red text-white shadow-sm" : "bg-green text-white shadow-sm"}
            info={<Text className="mt-0.5 text-xs text-gray-500">Last applied {lastApplied ? formatDate(lastApplied) : "never"}</Text>}
            rounded="lg"
            className="transition-shadow hover:shadow-md"
          />
          <MetricCard
            title="Statement run"
            metric={result ? sentenceCase(result.outcome) : "Not run from here"}
            icon={<MailIcon aria-hidden className="size-6" />}
            iconClassName="bg-blue text-white shadow-sm"
            info={
              <Text className="mt-0.5 text-xs text-gray-500">
                {result ? `${result.sent} sent · ${result.skipped} could not be sent` : "Sends only on the configured statement day"}
              </Text>
            }
            rounded="lg"
            className="transition-shadow hover:shadow-md"
          />
        </div>
      </PageHero>

      {runError ? <ErrorSurface error={runError} action="run the statement job" /> : null}

      <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <div className="@5xl:col-span-8">
          {excluded ? (
            <CaveatBanner
              caveat="These figures exclude every payment received. Receipts have not been applied, so this report shows customers who have already paid as still owing — it is the wrong list of people to chase, not a slightly stale one."
              action={
                <p>
                  Receipts last applied: <strong>{lastApplied ? formatDate(lastApplied) : "never"}</strong>. Receipts arrive through the Tally connector agent —{" "}
                  <Link to="/integrations/tally" className="underline">
                    check whether it is running
                  </Link>{" "}
                  before chasing anyone on this list.
                </p>
              }
            >
              {receivables}
            </CaveatBanner>
          ) : (
            receivables
          )}
        </div>

        <WidgetCard
          rounded="xl"
          className="@5xl:col-span-4"
          headerClassName="items-center"
          title={
            <CardTitle icon={<SendIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
              Monthly statements
            </CardTitle>
          }
        >
          <div className="mt-5 flex flex-col gap-5">
            <InfoBlock icon={<UsersIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Who gets one">
              <Text className="text-sm text-gray-700">Every B2B customer whose balance has moved since the last run.</Text>
            </InfoBlock>
            <InfoBlock icon={<CalendarIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="When it sends">
              <Text className="text-sm text-gray-700">Infra Junction decides whether today is the statement day. Run on any other date, it sends nothing.</Text>
            </InfoBlock>
            <InfoBlock icon={<MailIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Delivery">
              <Text className="text-sm text-gray-700">No email provider is configured yet, so a sent statement is logged rather than delivered.</Text>
            </InfoBlock>

            {excluded ? (
              <div role="alert">
                <Alert color="warning" variant="flat" bar>
                  <Text className="text-sm">
                    Receipts are not being applied, and a statement carries that caveat onto the statement itself. It goes to the person who paid, so settle the receipts pull first if you can.
                  </Text>
                </Alert>
              </div>
            ) : null}

            {result ? (
              <div role="status" className="flex flex-col gap-2 rounded-xl border border-muted bg-blue-lighter/30 p-4 dark:bg-blue-lighter/10">
                <Text className="text-xs font-semibold uppercase tracking-wider text-blue-dark">Statement run: {result.outcome.replaceAll("_", " ")}</Text>
                <Text className="text-sm text-gray-700">{EXPLANATION[result.outcome] ?? "Infra Junction reported an outcome this screen does not have words for."}</Text>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="flat" color="success" rounded="pill">
                    {result.sent} sent
                  </Badge>
                  <Badge variant="flat" color={result.skipped > 0 ? "warning" : "secondary"} rounded="pill">
                    {result.skipped} could not be sent
                  </Badge>
                </div>
                <Text className="text-xs text-gray-500">&quot;Could not be sent&quot; is a customer with a balance and no email address on file.</Text>
              </div>
            ) : null}

            {mayRun ? (
              <Button type="button" onClick={() => setConfirming(true)} disabled={running} isLoading={running} className="w-full">
                <SendIcon aria-hidden className="me-1.5 size-4" />
                Run the statement job
              </Button>
            ) : (
              <Text className="flex items-center gap-1.5 text-xs text-gray-500">
                <LockIcon aria-hidden className="size-3.5" />
                You don&apos;t have permission to run the statement job.
              </Text>
            )}
          </div>
        </WidgetCard>
      </div>

      {confirming ? (
        <ConfirmDialog
          title="Send statements to every eligible customer?"
          consequence="This emails a statement to every B2B customer whose balance has moved since the last run — real customers, if today is the configured statement day. It cannot be undone once sent."
          confirmLabel={running ? "Running…" : "Run the statement job"}
          cancel={
            <Button type="button" variant="outline" size="lg" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          }
          action={handleRun}
        />
      ) : null}
    </div>
  );
}

function ReceivablesTitle() {
  return (
    <CardTitle icon={<WalletIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
      Receivables by customer
    </CardTitle>
  );
}
